import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { List, ListItem, ListItemButton, ListItemText, Paper } from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';

import { formatDrugName, formatPackSize, formatStrength } from '../utils/format';

// Same composition as e-scribe-ui's getVariantSecondaryText: strength (unless
// "N/A"), pack size, form alias -- in that order, space-joined.
const describeVariant = ({ strength, packSizeValue, packSizeUnit, formAlias }) =>
  [
    strength && strength !== 'N/A' ? formatStrength(strength) : null,
    packSizeValue ? formatPackSize(packSizeValue, packSizeUnit) : null,
    formAlias,
  ]
    .filter(Boolean)
    .join(' ');

const CatalogList = ({ rows, loading, selectedCatalogId, onSelect }) => {
  const theme = useTheme();

  if (loading) {
    return <LoadingModule height='40vh' />;
  }

  if (isEmpty(rows)) {
    return <NothingFound height='30vh' customMessage='No catalog items found' />;
  }

  return (
    <Paper variant='outlined' sx={{ height: '100%', overflow: 'auto' }}>
      <List disablePadding dense>
        {rows.map((row) => (
          <ListItem key={row.catalogId} disableGutters>
            <ListItemButton
              sx={{
                minHeight: 48,
                bgcolor:
                  row.catalogId === selectedCatalogId
                    ? lighten(theme.palette.primary.main, 0.9)
                    : 'inherit',
              }}
              onClick={() => onSelect(row.catalogId)}
            >
              <ListItemText
                primary={formatDrugName(row.drugName)}
                secondary={describeVariant(row)}
              />
              {row.isActive ? <CheckIcon color='success' /> : <CloseIcon color='error' />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

CatalogList.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      catalogId: PropTypes.string.isRequired,
      drugName: PropTypes.string,
      isActive: PropTypes.bool,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  selectedCatalogId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

CatalogList.defaultProps = {
  selectedCatalogId: null,
};

export default CatalogList;
